import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import apiHooks from '../../../hooks/apiHooks';
import ListDataPage from '../../../components/pages/ListDataPage';
import dayjs from 'dayjs';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import { deleteSalesService } from '../../../services/salesServiceService';

interface ActionItem {
  eventKey: string;
  label: string;
  type: 'function' | 'redirect';
  callback?: () => void;
  url?: string;
  className?: string;
  divider?: boolean;
}

const AllServices = () => {
  const navigate = useNavigate();
  const { data: initialServices } = apiHooks.useSalesServices(); // Fetch initial services
  const [services, setServices] = useState(initialServices || []);

  // Sync state when new data comes from API
  useEffect(() => {
    setServices(initialServices || []);
  }, [initialServices]);

  // Function to confirm & delete service
  const handleDelete = async (service_id: string) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won’t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          await deleteSalesService(service_id);
          setServices(prev =>
            prev.filter(service => service.service_id !== service_id)
          ); // Update state

          Swal.fire({
            title: 'Deleted!',
            text: 'The service has been removed.',
            icon: 'success'
          });
        } catch (error) {
          Swal.fire({
            title: 'Error!',
            text: 'Failed to delete service.',
            icon: 'error'
          });
        }
      }
    });
  };

  // Define row actions
  const getActions = (service_id: string): ActionItem[] => [
    {
      eventKey: '2',
      label: 'Edit',
      type: 'function',
      callback: () => navigate(`/sales/services/edit-service/${service_id}`)
    },
    {
      eventKey: '3',
      label: 'Remove',
      type: 'function',
      callback: () => handleDelete(service_id), // Use sweet alert delete
      className: 'text-danger'
    },
    {
      eventKey: '4',
      label: 'Create Plan',
      type: 'function',
      callback: () => navigate(`/sales/services/edit-service/${service_id}`),
      className: 'text-primary'
    },
    {
      eventKey: '4',
      label: 'View',
      type: 'function',
      callback: () => navigate(`/sales/services/view-service/${service_id}`),
      className: 'text-primary'
    }
  ];

  const columns = [
    { accessorKey: 'service_id', header: 'Service ID' },
    { accessorKey: 'service_name', header: 'Service Name' },
    { accessorKey: 'description', header: 'Description' },
    { accessorKey: 'CDC_FLAG', header: 'Status' },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }: { row: any }) => (
        <span>
          {dayjs(row.original.created_at).format('DD/MM/YYYY HH:mm:ss')}
        </span>
      )
    },
    {
      id: 'action',
      header: 'Actions',
      cell: ({ row }: { row: any }) => (
        <RevealDropdownTrigger style={{ zIndex: '999' }}>
          <RevealDropdown>
            <ActionDropdownItems
              actions={getActions(row.original.service_id)}
            />
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-center' }
      }
    }
  ];

  return (
    <ListDataPage
      apiHook={() => ({ data: services, loading: false })} // Pass state as API response
      entityName="Services"
      columns={columns}
    />
  );
};

export default AllServices;
